import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Smartphone, Copy, Check, KeyRound, X } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Iter-363 — Authenticator-app (TOTP) 2FA, mirroring the reference screens.
 *   GET  /auth/2fa/totp/status
 *   POST /auth/2fa/totp/enroll  → { provisioning_uri, secret }
 *   POST /auth/2fa/totp/confirm {code} → { backup_codes[8] }  (shown ONCE)
 *   POST /auth/2fa/totp/disable {code}
 * Plus the account-tab adoption nudge (once per user, dismiss persisted).
 */

function errMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

export function TotpSecurityCard() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["totp-status"],
    retry: 0,
    queryFn: async () => (await sharedApi.get("/auth/2fa/totp/status")).data,
  });

  const [setup, setSetup] = useState(null); // { provisioning_uri, secret }
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null); // shown ONCE
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [copiedWhat, setCopiedWhat] = useState(null);

  const enabled = !!(statusQ.data?.enabled ?? statusQ.data?.totp_enabled);

  const enrollMut = useMutation({
    mutationFn: async () => (await sharedApi.post("/auth/2fa/totp/enroll", {})).data,
    onSuccess: (data) => setSetup(data),
    onError: (err) => toast.error(errMsg(err, "Couldn't start setup — try again.")),
  });

  const confirmMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post("/auth/2fa/totp/confirm", { code: confirmCode.trim() })).data,
    onSuccess: (data) => {
      setBackupCodes(data?.backup_codes || []);
      setSetup(null);
      setConfirmCode("");
      qc.invalidateQueries({ queryKey: ["totp-status"] });
      toast.success("Authenticator app is on.");
    },
    onError: (err) => toast.error(errMsg(err, "That code didn't match — try the current one.")),
  });

  const disableMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post("/auth/2fa/totp/disable", { code: disableCode.trim() })).data,
    onSuccess: () => {
      setDisableOpen(false);
      setDisableCode("");
      qc.invalidateQueries({ queryKey: ["totp-status"] });
      toast.success("Authenticator app turned off.");
    },
    onError: (err) => toast.error(errMsg(err, "That code didn't match.")),
  });

  const copy = async (what, text) => {
    if (await copyText(text)) {
      setCopiedWhat(what);
      setTimeout(() => setCopiedWhat(null), 2000);
    } else {
      toast.error("Couldn't copy — long-press the text instead.");
    }
  };

  if (statusQ.isError) return null; // endpoint not available — hide quietly

  return (
    <section className="mt-6 rounded-3xl bg-white shadow-card p-6" data-testid="totp-security-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Security</div>
          <div className="mt-2 flex items-center gap-2 text-sm font-bold text-slate-900">
            <Smartphone className="w-4 h-4 text-slate-400" /> Authenticator app
            {enabled && (
              <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5" data-testid="totp-on-badge">
                On
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Six-digit codes from an authenticator app protect your sign-in even if your SIM or inbox is compromised.
          </p>
        </div>
        {statusQ.isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
        ) : enabled ? (
          <button
            type="button"
            data-testid="totp-disable-toggle"
            onClick={() => setDisableOpen((v) => !v)}
            className="shrink-0 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold px-4 py-1.5 transition-colors"
          >
            Turn off
          </button>
        ) : !setup && !backupCodes ? (
          <button
            type="button"
            data-testid="totp-setup-btn"
            onClick={() => enrollMut.mutate()}
            disabled={enrollMut.isPending}
            className="shrink-0 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-60 transition-colors"
          >
            {enrollMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Set up"}
          </button>
        ) : null}
      </div>

      {/* Step 2 — setup key / link + confirm code */}
      {setup && !enabled && (
        <div className="mt-4 rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="totp-setup-panel">
          <p className="text-xs text-slate-600">
            Add Allsale to your authenticator app (Google Authenticator, Authy, 1Password…) using the setup key or link, then enter the current 6-digit code.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs font-bold tracking-widest text-slate-800 break-all" data-testid="totp-secret">
              {setup.secret}
            </code>
            <button
              type="button"
              data-testid="totp-copy-secret"
              onClick={() => copy("secret", setup.secret)}
              className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-blue)] hover:underline"
            >
              {copiedWhat === "secret" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy setup key
            </button>
            {setup.provisioning_uri && (
              <button
                type="button"
                data-testid="totp-copy-uri"
                onClick={() => copy("uri", setup.provisioning_uri)}
                className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-blue)] hover:underline"
              >
                {copiedWhat === "uri" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy setup link
              </button>
            )}
          </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!/^\d{6}$/.test(confirmCode.trim())) return toast.error("Enter the 6-digit code from the app.");
              confirmMut.mutate();
            }}
          >
            <input
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder="6-digit code"
              data-testid="totp-confirm-input"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm tracking-[0.3em] focus:outline-none focus:border-slate-500"
            />
            <button
              type="submit"
              disabled={confirmMut.isPending}
              data-testid="totp-confirm-submit"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-5 py-2 disabled:opacity-60 transition-colors"
            >
              {confirmMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm"}
            </button>
          </form>
        </div>
      )}

      {/* Backup codes — shown ONCE, never again */}
      {backupCodes && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="totp-backup-codes">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <KeyRound className="w-4 h-4 text-amber-600" /> Backup codes — save them now
          </div>
          <p className="mt-1 text-xs text-slate-600">
            Each code works once if you lose your authenticator. This is the only time they&apos;re shown.
          </p>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {backupCodes.map((c) => (
              <code key={c} className="rounded-lg bg-white border border-amber-200 px-2 py-1 text-[11px] font-bold tracking-wider text-slate-800 text-center">
                {c}
              </code>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              data-testid="totp-copy-backup"
              onClick={() => copy("backup", backupCodes.join("\n"))}
              className="inline-flex items-center gap-1 text-xs font-bold text-[color:var(--brand-blue)] hover:underline"
            >
              {copiedWhat === "backup" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy all
            </button>
            <button
              type="button"
              data-testid="totp-backup-done"
              onClick={() => setBackupCodes(null)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800"
            >
              I&apos;ve saved them
            </button>
          </div>
        </div>
      )}

      {/* Disable — requires a valid current code */}
      {disableOpen && enabled && (
        <form
          className="mt-4 flex items-center gap-2"
          data-testid="totp-disable-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (disableCode.trim().length < 6) return toast.error("Enter a current app code or a backup code.");
            disableMut.mutate();
          }}
        >
          <input
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9a-zA-Z]/g, "").slice(0, 12))}
            placeholder="Code from your app"
            data-testid="totp-disable-input"
            className="rounded-full border border-slate-300 px-4 py-2 text-sm tracking-[0.2em] focus:outline-none focus:border-slate-500"
          />
          <button
            type="submit"
            disabled={disableMut.isPending}
            data-testid="totp-disable-submit"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-5 py-2 disabled:opacity-60 transition-colors"
          >
            {disableMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Turn off"}
          </button>
        </form>
      )}
    </section>
  );
}

/** Account-tab adoption nudge — once per user, dismiss persisted. */
export function TotpNudge() {
  const { user } = useAuth();
  const key = user?.id ? `allsale.totpNudge.${user.id}` : null;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return key ? localStorage.getItem(key) === "1" : true;
    } catch {
      return true;
    }
  });
  const statusQ = useQuery({
    queryKey: ["totp-status"],
    retry: 0,
    enabled: !dismissed,
    queryFn: async () => (await sharedApi.get("/auth/2fa/totp/status")).data,
  });
  const enabled = !!(statusQ.data?.enabled ?? statusQ.data?.totp_enabled);
  if (dismissed || statusQ.isLoading || statusQ.isError || enabled) return null;

  const dismiss = () => {
    try {
      if (key) localStorage.setItem(key, "1");
    } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div className="mt-4 rounded-2xl border border-[color:var(--brand-blue)] bg-[color:var(--brand-blue-wash)] px-4 py-3 flex items-center gap-3" data-testid="totp-nudge">
      <ShieldCheck className="w-5 h-5 text-[color:var(--brand-blue)] shrink-0" />
      <p className="flex-1 text-xs text-slate-700">
        <span className="font-bold">Add an authenticator app</span> — the strongest way to protect your account. Takes under a minute, right below in Security.
      </p>
      <button type="button" onClick={dismiss} data-testid="totp-nudge-dismiss" className="text-slate-400 hover:text-slate-700">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
